import { clsx } from "clsx";
import { XIcon } from "lucide-react";
import type { FocusEvent, MouseEvent } from "react";
import { useCallback, useMemo } from "react";
import { useTranslations } from "../../i18n";
import { textFieldButtonSize } from "../../utils";
import { IconButton } from "../Button/IconButton";
import { useDropdownControlProps } from "../Dropdown";
import { FieldAccessory } from "../FieldAccessory";
import type { ISelectOption } from "../Select/types";
import { Spinner } from "../Spinner";
import type { ISpinnerSize } from "../Spinner/types";
import { Tag } from "../Tag";
import type { IComboBoxSize, IComboBoxTriggerProps } from "./types";
import { useComboBoxTrigger } from "./useComboBoxTrigger";

const spinnerSizeMap: Record<IComboBoxSize, ISpinnerSize> = {
  sm: "xs",
  md: "sm",
  lg: "md",
};

/**
 * The box positions the panel and takes pointer events. The input holds the
 * `combobox` role, so the tag remove buttons and the clear button are its
 * siblings, never its descendants.
 */
export const ComboBoxMultiTrigger = ({
  ref,
  onClick,
  className: _className,
  ...anchorProps
}: IComboBoxTriggerProps) => {
  const { t } = useTranslations();

  const {
    disabled,
    readOnly,
    size,
    placeholder,
    inputId,
    searchValue,
    setSearchValue,
    onClear,
    showClearButton,
    invalid,
    loading,
    onSelect,
    multiple,
    value,
    activeDescId,
    onInputKeyDownCapture,
    onInputFocus,
    dataAttributes,
    searchInputRef,
  } = useComboBoxTrigger();
  const controlProps = useDropdownControlProps();

  const selectedOptions = useMemo((): ISelectOption[] => {
    if (!multiple || !value) {
      return [];
    }

    return Array.isArray(value) ? value : [value];
  }, [multiple, value]);

  const handleBadgeRemove = useCallback(
    (optionId: string) => {
      const optionToRemove = selectedOptions.find((opt) => opt.id === optionId);
      if (optionToRemove) {
        onSelect(optionToRemove);
        searchInputRef.current?.focus();
      }
    },
    [selectedOptions, onSelect, searchInputRef],
  );

  const handleClear = useCallback(() => {
    onClear?.();
    searchInputRef.current?.focus();
  }, [onClear, searchInputRef]);

  const handleClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      onClick?.(e);
      searchInputRef.current?.focus();
    },
    [onClick, searchInputRef],
  );

  // The box is focusable only so a click on its padding lands somewhere; pass it on.
  const handleBoxFocus = useCallback(
    (e: FocusEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        searchInputRef.current?.focus();
      }
    },
    [searchInputRef],
  );

  const canEdit = !disabled && !readOnly;

  const clearButtonJSX = showClearButton && (
    <IconButton
      variant="ghost"
      size={textFieldButtonSize[size]}
      color="neutral"
      onClick={handleClear}
      content={<XIcon />}
      aria-label={t("ui.comboBox.clearSelection")}
    />
  );

  const spinnerJSX = !disabled && loading && (
    <FieldAccessory size={size}>
      <Spinner color="neutral" size={spinnerSizeMap[size]} />
    </FieldAccessory>
  );

  const rootClass = clsx(
    "nuco-field nuco-combobox__trigger nuco-combobox__trigger-multi",
    `nuco-combobox__trigger--${size}`,
    invalid && "nuco-field--invalid nuco-color-danger",
    disabled && "nuco-field--disabled",
    readOnly && "nuco-field--readOnly",
  );

  return (
    <div
      {...anchorProps}
      ref={ref}
      className={rootClass}
      onClick={handleClick}
      onFocus={handleBoxFocus}
    >
      <div className="nuco-combobox__trigger-inner">
        {selectedOptions.map((option) => (
          <Tag
            key={option.id}
            id={option.id}
            text={option.text}
            startIcon={option.icon}
            color="neutral"
            size={size}
            onRemove={canEdit ? () => handleBadgeRemove(option.id) : undefined}
            disabled={disabled}
          />
        ))}
        <input
          {...controlProps}
          ref={searchInputRef}
          id={inputId}
          {...dataAttributes}
          aria-autocomplete="list"
          aria-activedescendant={activeDescId}
          className="nuco-field__control nuco-combobox__trigger-input nuco-text nuco-text--md"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onFocus={onInputFocus}
          onKeyDownCapture={onInputKeyDownCapture}
          disabled={disabled}
          readOnly={readOnly}
          aria-invalid={invalid}
          placeholder={placeholder}
          autoComplete="off"
        />
      </div>
      <div className="nuco-combobox__trigger-actions">
        {spinnerJSX || clearButtonJSX}
      </div>
    </div>
  );
};
